import { BaseCharacterService } from './BaseCharacterService'
import { getLoadedStore } from '@/stores/storeRegistry'
import { targetedCloneForMutation } from '@/utils/cloneHelpers'

// Minimal helper referenced in this file; kept as no-op to avoid altering behavior
function ensureArrayIndexes(arr) {
  if (!Array.isArray(arr)) return

  // Build set of used indexes to avoid collisions
  const used = new Set()
  for (const it of arr) {
    if (it && typeof it === 'object' && it.index) used.add(String(it.index))
  }

  const slugify = (s) =>
    String(s || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')

  const makeBase = (it) => {
    if (!it || typeof it !== 'object')
      return `idx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    if (it.id) return String(it.id)
    if (it.name) return slugify(it.name)
    return `idx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  }

  const pickUnique = (base) => {
    let candidate = String(base)
    if (!candidate) candidate = `idx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    if (!used.has(candidate)) {
      used.add(candidate)
      return candidate
    }
    let i = 2
    while (true) {
      const c = `${candidate}-${i}`
      if (!used.has(c)) {
        used.add(c)
        return c
      }
      i++
    }
  }

  for (const it of arr) {
    if (it && typeof it === 'object') {
      if (it.index === undefined || it.index === null || it.index === '') {
        const base = makeBase(it)
        it.index = pickUnique(base)
      }
    }
  }
}

/**
 * D&D 5e Character Service - Contains 5e-specific functionality
 */
export class DnD5eCharacterService extends BaseCharacterService {
  constructor() {
    super()
    // xpSignal removed: rely on xp store reactivity and character fields
    // Proactively warm the XP store so consumers have thresholds immediately
    // MOVED: Calling this in constructor causes circular dependency crash during app boot
    // The store will be initialized lazily when first accessed
    /*
    try {
      stores.useXpStore?.().catch(() => {})
    } catch {
      // ignore
    }
    */
  }

  /**
   * Provide a safe empty character template for D&D 5e
   * Returns minimal fields required for the UI to render without errors.
   */
  getEmptyCharacter() {
    const now = new Date().toISOString()
    return {
      id: null,
      name: '',
      // Sheet selector will set an object { id: 'dnd_5e', name: 'D&D 5th Edition' }
      // Keeping a simple string here avoids coupling and is normalized on save
      gameVersion: '5e',

      // Core progression
      level: 1,
      xp: 0,
      progressionSpeed: 'normal',

      // Identity
      race: '',
      subrace: '',
      background: '',
      alignment: '',

      // Classes (BasicInfoTab will ensure at least one entry)
      classes: [],

      // Abilities: short keys by default; consumers normalize as needed
      abilities: {
        str: 10,
        dex: 10,
        con: 10,
        int: 10,
        wis: 10,
        cha: 10
      },

      // Skills array (SkillsTab expects an array of { name, isProficient, customModifier })
      skills: [],

      // Combat basics
      hitPoints: { maximum: 0, current: 0, temporary: 0 },
      armorClass: null, // computed from dex by default
      initiative: null, // computed from dex by default
      proficiencyBonus: undefined, // may be overridden manually
      inspiration: false,
      speed: '30 ft',
      flySpeed: '',
      hitDice: '',
      passivePerception: undefined,
      exhaustion: 0,
      deathSaves: { successes: [false, false, false], failures: [false, false, false] },

      // Saving throws per ability (proficiency toggle; optional manual value/bonus)
      savingThrows: {
        str: { proficient: false },
        dex: { proficient: false },
        con: { proficient: false },
        int: { proficient: false },
        wis: { proficient: false },
        cha: { proficient: false }
      },

      // Equipment and inventory
      inventory: [],
      equipment: { armor: null, shield: false, items: [] },
      resistances: [],
      immunities: [],
      conditions: [],

      // Features and traits
      features: [],
      feats: [],
      traits: [],

      // Languages and proficiencies (free-text lists for UI)
      languages: [],
      proficiencies: [],

      // Spell data containers (tabs sanitize/normalize on demand)
      spells: {},
      customSpells: {},

      // Personality/notes
      personalityTraits: { traits: '', ideals: '', bonds: '', flaws: '' },
      backstory: '',
      notes: [],

      // Media
      avatar: '',

      // Timestamps
      createdAt: now,
      updatedAt: now
    }
  }

  /**
   * Normalize a user typed class name to a canonical option from a list
   * @param {string} className
   * @param {Array<string|Object>} availableClasses optional list, otherwise class store is used
   * @returns {string} canonical class name if found, otherwise original
   */
  canonicalizeClassName(className, availableClasses) {
    if (!className) return className
    const typed = String(className).trim()
    if (!typed) return className
    let list = []
    if (Array.isArray(availableClasses) && availableClasses.length) list = availableClasses
    else {
      try {
        const cs = getLoadedStore('class')
        list = cs && cs.getClasses ? cs.getClasses : []
      } catch {
        list = []
      }
    }
    const needle = typed.toLowerCase()
    const match = (Array.isArray(list) ? list : []).find(
      (c) =>
        String(c || '')
          .trim()
          .toLowerCase() === needle
    )
    return match || className
  }

  /**
   * Determine if a class name is custom (not in canonical classes list)
   * @param {string} className
   * @returns {boolean}
   */
  isCustomClass(className) {
    if (!className) return false
    try {
      const cs = getLoadedStore('class')
      if (!cs) return true // If store not loaded, assume it's custom for safety
      const list = cs ? (Array.isArray(cs.getClasses) ? cs.getClasses : cs.getClasses || []) : []
      const needle = String(className).trim().toLowerCase()
      return !list.find(
        (c) =>
          String(c || '')
            .trim()
            .toLowerCase() === needle
      )
    } catch {
      return false
    }
  }

  /**
   * Get subclass option strings for the provided className (delegates to class store)
   * @param {string} className
   * @returns {Array<string>} list of subclass names or []
   */
  async getSubclassOptions(className) {
    if (!className) return []
    try {
      // Use same pattern as fetchClassDetails to access the store
      const { useClassStore } = await import('@/stores/versions/dnd_5e/classStore')
      const cs = useClassStore()
      if (!cs) return [] // Store not available, return empty array
      // Try to use store getter if details cached
      const classKey = String(className).toLowerCase().replace(/\s+/g, '-')
      const details = cs.getClassDetails ? cs.getClassDetails(classKey) : undefined
      if (details && Array.isArray(details.subclasses)) {
        const subclassNames = details.subclasses
          .map((s) => (typeof s === 'string' ? s : s.name || ''))
          .filter(Boolean)
        return subclassNames
      }
    } catch (error) {
      console.error('[CharacterService] getSubclassOptions error:', error)
    }
    return []
  }

  /**
   * Fetch class details via classStore.fetchClassDetails and return details
   * @param {string} className
   * @returns {Promise<Object>} Class details object
   */
  async fetchClassDetails(className) {
    if (!className) return null
    try {
      // Use proper async store loading instead of getLoadedStore
      const { useClassStore } = await import('@/stores/versions/dnd_5e/classStore')
      const cs = useClassStore()
      const classKey = String(className).toLowerCase().replace(/\s+/g, '-')
      // Delegate to class store fetchClassDetails
      if (typeof cs.fetchClassDetails === 'function') {
        await cs.fetchClassDetails(classKey)
        // Use getter to return fresh details
        return cs.getClassDetails ? cs.getClassDetails(classKey) : null
      }
    } catch (err) {
      console.warn('Error fetching class details:', err)
    }
    return null
  }

  /**
   * Ensure array indexes for any nested items in the character schema
   * @param {Object} character Character object
   */
  _ensureIndexesFromSchema(character) {
    if (!character) return
    // Ensure top-level arrays are indexed
    if (Array.isArray(character.items)) ensureArrayIndexes(character.items)
    if (Array.isArray(character.features)) ensureArrayIndexes(character.features)
    if (Array.isArray(character.spells)) ensureArrayIndexes(character.spells)
    if (Array.isArray(character.classes)) ensureArrayIndexes(character.classes)
    if (Array.isArray(character.traits)) ensureArrayIndexes(character.traits)
    if (Array.isArray(character.inventory)) ensureArrayIndexes(character.inventory)
    if (Array.isArray(character.equipment)) ensureArrayIndexes(character.equipment)
    if (Array.isArray(character.proficiencies)) ensureArrayIndexes(character.proficiencies)
    if (Array.isArray(character.languages)) ensureArrayIndexes(character.languages)
    if (Array.isArray(character.equipment)) ensureArrayIndexes(character.equipment)
    if (Array.isArray(character.items)) ensureArrayIndexes(character.items)

    // Ensure nested items in containers are also indexed
    if (Array.isArray(character.inventory)) {
      for (const item of character.inventory) {
        if (item && item.type === 'container' && Array.isArray(item.items)) {
          ensureArrayIndexes(item.items)
        }
      }
    }
  }

  // rely on BaseCharacterService._ensureIndexesFromSchema

  /**
   * Import character from JSON data and validate against schema
   * @param {string} jsonData JSON data (string or object)
   * @returns {Object} Imported character object (validated)
   */
  async importCharacter(jsonData) {
    // If input is already an object, stringify then parse to normalize
    let parsed
    try {
      parsed = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData
    } catch {
      throw new Error('Invalid JSON provided for import')
    }

    // Assign a provisional id and timestamp
    parsed.id =
      parsed.id || 'imported_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    parsed.importedAt = new Date().toISOString()

    // Validate against schema if available
    try {
      const SchemaService = (await import('@/services/SchemaService')).default
      const key = 'character:5e'
      const res = await SchemaService.validate(key, parsed)
      if (!res.valid) {
        const msg = (res.errors || [])
          .slice(0, 10)
          .map((e) => e.message || JSON.stringify(e))
          .join('; ')
        throw new Error('Schema validation failed: ' + msg)
      }
    } catch (err) {
      // If validation explicitly failed, rethrow so the caller can handle it
      if (err && err.message && err.message.includes('Schema validation failed')) {
        throw err
      }
      // Otherwise warn and continue (schema not required)
      console.warn('Schema validation not available or failed to run:', err && err.message)
    }

    return parsed
  }

  /**
   * Get character by ID as-is from the database
   * @param {string} characterId Character ID
   * @returns {Promise<Object>} Character object
   */
  async getCharacter(characterId) {
    return super.getCharacter(characterId)
  }

  /**
   * Get characters by user ID as-is from the database
   * @param {string} userId User ID
   * @returns {Promise<Array>} Array of character objects
   */
  async getCharacters(userId) {
    return super.getCharacters(userId)
  }

  /**
   * Calculate proficiency bonus for D&D 5e
   * @param {number} level Character level
   * @returns {number} Proficiency bonus
   */
  calculateProficiencyBonus(level) {
    return Math.floor((level - 1) / 4) + 2
  }

  /**
   * Get maximum allowed ability score
   * @returns {number} Maximum ability score
   */
  getMaxAbilityScore() {
    return 99
  }

  /**
   * Calculate ability score modifier for D&D 5e
   * @param {number} score Ability score
   * @returns {string} Formatted ability modifier
   */
  getAbilityModifier(score) {
    const mod = Math.floor((score - 10) / 2)
    return mod >= 0 ? `+${mod}` : `${mod}`
  }

  /**
   * Get ability modifier value (not formatted) for D&D 5e
   * @param {number} score Ability score
   * @returns {number} Modifier value
   */
  getAbilityModifierValue(score) {
    return Math.floor((score - 10) / 2)
  }

  /**
   * Infer spellcasting ability key from character (version specific logic)
   * @param {Object} character
   * @returns {string} ability key (str,dex,con,int,wis,cha) defaults to int
   */
  inferSpellcastingAbility(character) {
    if (!character) return 'int'
    if (character.spellcasting?.spellcastingAbility)
      return character.spellcasting.spellcastingAbility
    if (character.spellcastingAbility) return character.spellcastingAbility
    if (Array.isArray(character.classes) && character.classes.length) {
      const primary = (character.classes[0].name || '').toLowerCase()
      if (['bard', 'paladin', 'sorcerer', 'warlock'].includes(primary)) return 'charisma'
      if (['artificer', 'wizard'].includes(primary)) return 'intelligence'
      if (['cleric', 'druid', 'ranger'].includes(primary)) return 'wisdom'
    }
    return 'intelligence'
  }

  /**
   * Get spellcasting derived context (auto DC, attack bonus, components)
   * @param {Object} character
   * @returns {Object} { abilityKey, abilityScore, abilityMod, proficiency, dcAuto, attackAuto }
   */
  getSpellcastingContext(character) {
    const abilityLong = this.inferSpellcastingAbility(character)
    const abilityMap = {
      strength: 'str',
      dexterity: 'dex',
      constitution: 'con',
      intelligence: 'int',
      wisdom: 'wis',
      charisma: 'cha',
      str: 'str',
      dex: 'dex',
      con: 'con',
      int: 'int',
      wis: 'wis',
      cha: 'cha'
    }
    const abilityKey = abilityMap[abilityLong] || 'int'
    const abilityScore = character?.abilities?.[abilityKey] || 10
    const abilityMod = this.getAbilityModifierValue(abilityScore)
    const totalLevel = this.getTotalLevel(character)
    const proficiency = this.calculateProficiencyBonus(totalLevel)
    const dcAuto = 8 + proficiency + abilityMod
    const attackAuto = proficiency + abilityMod
    return { abilityKey, abilityScore, abilityMod, proficiency, dcAuto, attackAuto }
  }

  /**
   * Calculate skill modifier for D&D 5e
   * @param {Object} character Character object
   * @param {string} skillName Skill name
   * @returns {number} Skill modifier
   */
  getSkillModifier(character, skillName) {
    const skillMap = {
      acrobatics: 'dexterity',
      animalHandling: 'wisdom',
      arcana: 'intelligence',
      athletics: 'strength',
      deception: 'charisma',
      history: 'intelligence',
      insight: 'wisdom',
      intimidation: 'charisma',
      investigation: 'intelligence',
      medicine: 'wisdom',
      nature: 'intelligence',
      perception: 'wisdom',
      performance: 'charisma',
      persuasion: 'charisma',
      religion: 'intelligence',
      sleightOfHand: 'dexterity',
      stealth: 'dexterity',
      survival: 'wisdom'
    }

    const ability = skillMap[skillName]
    if (!ability) return 0

    // Support both long-form (wisdom) and short-form (wis) ability keys
    const shortKeyMap = {
      strength: 'str',
      dexterity: 'dex',
      constitution: 'con',
      intelligence: 'int',
      wisdom: 'wis',
      charisma: 'cha'
    }
    const abilities = character?.abilities || {}
    const abilityScore = Number(abilities[ability] ?? abilities[shortKeyMap[ability]] ?? 10)
    const abilityMod = this.getAbilityModifierValue(abilityScore)
    const totalLevel =
      typeof this.getTotalLevel === 'function'
        ? this.getTotalLevel(character)
        : character?.level || 1
    const proficiencyBonus = this.calculateProficiencyBonus(totalLevel)

    const skill = character?.skills ? character.skills[skillName] : undefined
    let modifier = abilityMod

    if (skill) {
      if (typeof skill === 'object') {
        if (skill.proficient) modifier += proficiencyBonus
        if (skill.expertise) modifier += proficiencyBonus // adds an extra proficiency (total 2x)
        if (typeof skill.bonus === 'number') modifier += Number(skill.bonus)
      } else if (typeof skill === 'number') {
        // numeric legacy format: multiplier (0,1,2) of proficiency
        modifier += Math.floor(skill * proficiencyBonus)
      } else if (typeof skill === 'boolean' && skill === true) {
        modifier += proficiencyBonus
      }
    }

    return modifier
  }

  /**
   * Calculate saving throw modifier for D&D 5e
   * @param {Object} character Character object
   * @param {string} ability Ability name
   * @returns {number} Saving throw modifier
   */
  getSavingThrowModifier(character, ability) {
    try {
      if (!character?.abilities) return 0
      const ab = String(ability).toLowerCase()
      const abilityScore = character.abilities?.[ab]
      const abilityMod = this.getAbilityModifierValue(abilityScore)

      const st = character.savingThrows?.[ab] || {}

      // If a manual value is set, it takes precedence
      if (typeof st.value === 'number') return Number(st.value)

      // Extra flat bonus if present
      const extra = typeof st.bonus === 'number' ? Number(st.bonus) : 0

      // Resolve proficiency bonus (manual override or derived from level)
      let prof =
        typeof character.proficiencyBonus === 'number'
          ? Number(character.proficiencyBonus)
          : this.calculateProficiencyBonus(
              (this.getTotalLevel ? this.getTotalLevel(character) : character.level) || 1
            )

      const addProf = st.proficient ? prof : 0
      return abilityMod + addProf + extra
    } catch {
      return 0
    }
  }

  /**
   * Calculate spell save DC for D&D 5e
   * @param {Object} character Character object
   * @returns {number} Spell save DC
   */
  getSpellSaveDC(character) {
    if (!character) return 8

    // Manual override (version specific field inside spells object)
    if (
      character.spells?.auto_spell_dc === false &&
      typeof character.spells?.spell_save_dc === 'number'
    ) {
      return character.spells.spell_save_dc
    }

    const { dcAuto } = this.getSpellcastingContext(character)
    return dcAuto
  }

  /**
   * Calculate spell attack bonus for D&D 5e
   * @param {Object} character Character object
   * @returns {number} Spell attack bonus
   */
  getSpellAttackBonus(character) {
    if (!character) return 0

    // Manual override (version specific field inside spells object)
    if (
      character.spells?.auto_spell_attack === false &&
      typeof character.spells?.spell_attack_bonus === 'number'
    ) {
      return character.spells.spell_attack_bonus
    }

    const { attackAuto } = this.getSpellcastingContext(character)
    return attackAuto
  }

  /**
   * Calculate hit point maximum for D&D 5e
   * @param {Object} character Character object
   * @returns {number} Hit point maximum
   */
  getHitPointMaximum(character) {
    if (!character.classes || character.classes.length === 0) return 8

    const constitutionMod = this.getAbilityModifierValue(character.abilities.constitution)

    // Calculate hit points based on class hit dice
    let hitPoints = 0

    character.classes.forEach((characterClass, index) => {
      const classLevel = characterClass.level
      const hitDie = this.getClassHitDie(characterClass.name)

      if (index === 0) {
        // First class gets max hit die at level 1
        hitPoints += hitDie + constitutionMod

        // Remaining levels get average (rounded up)
        if (classLevel > 1) {
          hitPoints +=
            (classLevel - 1) * (Math.floor(hitDie / 2) + 1) + (classLevel - 1) * constitutionMod
        }
      } else {
        // Multiclass levels get average
        hitPoints += classLevel * (Math.floor(hitDie / 2) + 1) + classLevel * constitutionMod
      }
    })

    return Math.max(1, hitPoints)
  }

  /**
   * Get hit die for a class
   * @param {string} className Class name
   * @returns {number} Hit die size
   */
  getClassHitDie(className) {
    const hitDieMap = {
      Barbarian: 12,
      Fighter: 10,
      Paladin: 10,
      Ranger: 10,
      Bard: 8,
      Cleric: 8,
      Druid: 8,
      Monk: 8,
      Rogue: 8,
      Warlock: 8,
      Sorcerer: 6,
      Wizard: 6
    }

    return hitDieMap[className] || 8
  }

  /**
   * Calculate armor class for D&D 5e
   * @param {Object} character Character object
   * @returns {number} Armor class
   */
  getArmorClass(character) {
    // Base AC is 10 + Dex modifier (support both dexterity and dex keys)
    const abilities = character && character.abilities ? character.abilities : {}
    const dexScore = Number(abilities.dexterity ?? abilities.dex ?? 10)
    const dexMod = this.getAbilityModifierValue(dexScore)
    const acBase = 10 + dexMod

    // Use stored armorClass only if it is a finite number > 0; otherwise fallback to computed base
    const stored = character ? character.armorClass : undefined
    const storedNum = Number(stored)
    if (Number.isFinite(storedNum) && storedNum > 0) {
      return storedNum
    }

    // TODO: Add detailed armor calculations based on equipped armor/shields and class features
    return acBase
  }

  /**
   * Calculate initiative modifier for D&D 5e
   * @param {Object} character Character object
   * @returns {number} Initiative modifier
   */
  getInitiativeModifier(character) {
    return this.getAbilityModifierValue(character.abilities.dexterity)
  }

  /**
   * Get spell slots for a character level and class
   * @param {Object} character Character object
   * @returns {Object} Spell slots by level
   */
  getSpellSlots(character) {
    if (!character.classes || character.classes.length === 0) {
      return {
        1: { total: 0, used: 0 },
        2: { total: 0, used: 0 },
        3: { total: 0, used: 0 },
        4: { total: 0, used: 0 },
        5: { total: 0, used: 0 },
        6: { total: 0, used: 0 },
        7: { total: 0, used: 0 },
        8: { total: 0, used: 0 },
        9: { total: 0, used: 0 }
      }
    }

    // TODO: Implement spell slot calculation based on class and level
    // For now, return the character's stored spell slots
    return (
      character.spellcasting?.spellSlots || {
        1: { total: 0, used: 0 },
        2: { total: 0, used: 0 },
        3: { total: 0, used: 0 },
        4: { total: 0, used: 0 },
        5: { total: 0, used: 0 },
        6: { total: 0, used: 0 },
        7: { total: 0, used: 0 },
        8: { total: 0, used: 0 },
        9: { total: 0, used: 0 }
      }
    )
  }

  /**
   * Get class features for a character
   * @param {Object} character Character object
   * @returns {Array} Array of class features
   */
  getClassFeatures(character) {
    // TODO: Implement class feature lookup based on class and level
    return character.features || []
  }

  /**
   * Get race traits for a character
   * @param {Object} character Character object
   * @returns {Array} Array of race traits
   */
  getRaceTraits(character) {
    // TODO: Implement race trait lookup based on race and subrace
    return character.traits || []
  }

  /**
   * Check if character can cast spells
   * @param {Object} character Character object
   * @returns {boolean} True if character can cast spells
   */
  canCastSpells(character) {
    if (!character.classes || character.classes.length === 0) return false

    const spellcastingClasses = [
      'Bard',
      'Cleric',
      'Druid',
      'Sorcerer',
      'Warlock',
      'Wizard',
      'Paladin',
      'Ranger'
    ]

    return character.classes.some((characterClass) =>
      spellcastingClasses.includes(characterClass.name)
    )
  }

  /**
   * Get carrying capacity for D&D 5e
   * @param {Object} character Character object
   * @returns {number} Carrying capacity in pounds
   */
  getCarryingCapacity(character) {
    const strength = character.abilities.strength
    return strength * 15
  }

  /** Sync XP progression speed in store from character if provided */
  _syncProgressionSpeed(character) {
    try {
      const xpStore = getLoadedStore('xp')
      const speed = character?.progressionSpeed
      if (!xpStore) return
      if (speed === 'slow') xpStore.setSlowProgression()
      else if (speed === 'medium') xpStore.setMediumProgression()
      else xpStore.resetProgressionSpeed()
      // rely on xp store reactivity and character fields; no explicit signal bump
    } catch {
      // ignore if store not available in this context
    }
  }

  /** Get next level XP threshold (or 'Max Level' at 20) */
  getNextLevelXP(character) {
    this._syncProgressionSpeed(character)
    const level =
      typeof this.getTotalLevel === 'function'
        ? this.getTotalLevel(character)
        : character?.level || 1
    if (level > 20) return 'Epic'
    if (level === 20) return 'Max Level'
    const xpStore = getLoadedStore('xp')
    return xpStore && typeof xpStore.getThresholdForLevel === 'function'
      ? xpStore.getThresholdForLevel(level + 1) || 0
      : 0
  }

  // Expose a method to get the threshold for an arbitrary level (1-based)
  getThresholdForLevel(level) {
    const xpStore = getLoadedStore('xp')
    if (xpStore && typeof xpStore.getThresholdForLevel === 'function')
      return xpStore.getThresholdForLevel(level)
    return 0
  }

  /** Get experience progress [0..1] for current level */
  getExperienceProgress(character) {
    // Ensure store multiplier is in sync with character progressionSpeed
    this._syncProgressionSpeed(character)
    const level =
      typeof this.getTotalLevel === 'function'
        ? this.getTotalLevel(character)
        : character?.level || 1
    if (level >= 20) return 1
    const currentXP = Number(character?.xp ?? character?.experience ?? 0) || 0
    const xpStore = getLoadedStore('xp')
    return xpStore && typeof xpStore.getExperienceProgress === 'function'
      ? xpStore.getExperienceProgress(currentXP, level)
      : 0
  }

  // Diagnostic helper: returns detailed experience progress information
  getExperienceProgressDetails(character) {
    try {
      const xpStore = getLoadedStore('xp')
      const level =
        typeof this.getTotalLevel === 'function'
          ? this.getTotalLevel(character)
          : character?.level || 1
      const currentXP = Number(character?.xp ?? character?.experience ?? 0) || 0
      if (xpStore && typeof xpStore.getExperienceProgressDetails === 'function')
        return xpStore.getExperienceProgressDetails(currentXP, level)
      // If store not ready, provide minimal info
      const progress = this.getExperienceProgress(character)
      return { level, currentXP, progress }
    } catch {
      return { progress: this.getExperienceProgress(character) }
    }
  }

  // Returns XP remaining to reach next level (0 if at or above next level or at max level)
  getRemainingXPForNextLevel(character) {
    try {
      const xpStore = getLoadedStore('xp')
      const level =
        typeof this.getTotalLevel === 'function'
          ? this.getTotalLevel(character)
          : character?.level || 1
      if (level >= 20) return 0
      const numericCurrentXP = Number(character?.xp ?? character?.experience ?? 0) || 0
      if (xpStore && typeof xpStore.getRemainingXP === 'function')
        return xpStore.getRemainingXP(numericCurrentXP, level)
      return 0
    } catch {
      return 0
    }
  }

  setProgressionSpeed(characterOrSpeed) {
    try {
      const xpStore = getLoadedStore('xp')
      let speed = characterOrSpeed
      if (typeof characterOrSpeed === 'object') speed = characterOrSpeed?.progressionSpeed
      if (xpStore) {
        if (speed === 'slow') xpStore.setSlowProgression()
        else if (speed === 'medium') xpStore.setMediumProgression()
        else xpStore.resetProgressionSpeed()
        return true
      }

      // If the store isn't loaded yet, try to initialize it asynchronously and apply the change when ready
      import('@/stores/versions/dnd_5e/xpStore')
        .then((m) => m.useXpStore())
        .then((s) => {
          if (!s) return
          if (speed === 'slow') s.setSlowProgression()
          else if (speed === 'medium') s.setMediumProgression()
          else s.resetProgressionSpeed()
        })
        .catch(() => {})

      return true
    } catch {
      return false
    }
  }

  /**
   * Get push/drag/lift capacity for D&D 5e
   * @param {Object} character Character object
   * @returns {number} Push/drag/lift capacity in pounds
   */
  getPushDragLiftCapacity(character) {
    return this.getCarryingCapacity(character) * 2
  }

  /**
   * Calculate passive perception for D&D 5e
   * @param {Object} character Character object
   * @returns {number} Passive perception score
   */
  getPassivePerception(character) {
    if (!character) return 10

    // Base from skill modifier which includes ability mod and proficiency/expertise
    const perceptionMod = this.getSkillModifier(character, 'perception')

    // Additional bonuses: explicit bonus field or feats/traits like Observant (+5)
    let extra = 0
    // Allow explicit per-skill bonus: character.skills?.perception?.bonus
    try {
      const explicit = character?.skills?.perception
      if (explicit && typeof explicit === 'object' && typeof explicit.bonus === 'number')
        extra += Number(explicit.bonus)
    } catch {
      // ignore
    }

    // Observant feat: look for a feature/feat named 'Observant' (case-insensitive)
    try {
      const features = Array.isArray(character.features)
        ? character.features
        : Array.isArray(character.traits)
          ? character.traits
          : []
      if (features && features.length) {
        for (const f of features) {
          const name = f && (f.name || f) ? String(f.name || f) : ''
          if (name && name.toLowerCase().includes('observant')) {
            extra += 5
            break
          }
        }
      }
    } catch {
      // ignore
    }

    return 10 + perceptionMod + extra
  }

  /**
   * Calculate passive investigation for D&D 5e
   * @param {Object} character Character object
   * @returns {number} Passive investigation score
   */
  getPassiveInvestigation(character) {
    const investigationMod = this.getSkillModifier(character, 'investigation')
    return 10 + investigationMod
  }

  /**
   * Calculate passive insight for D&D 5e
   * @param {Object} character Character object
   * @returns {number} Passive insight score
   */
  getPassiveInsight(character) {
    const insightMod = this.getSkillModifier(character, 'insight')
    return 10 + insightMod
  }

  /**
   * Get all passive scores for a character
   * @param {Object} character Character object
   * @returns {Object} Passive scores
   */
  getPassiveScores(character) {
    return {
      perception: this.getPassivePerception(character),
      investigation: this.getPassiveInvestigation(character),
      insight: this.getPassiveInsight(character)
    }
  }

  /**
   * Validate character data for D&D 5e
   * @param {Object} character Character object
   * @returns {Array} Array of validation errors
   */
  validateCharacter(character) {
    const errors = []

    if (!character.name || character.name.trim() === '') {
      errors.push('Character name is required')
    }

    if (!character.race || character.race.trim() === '') {
      errors.push('Character race is required')
    }

    if (!character.classes || character.classes.length === 0) {
      errors.push('Character must have at least one class')
    }

    if (character.level < 1) {
      errors.push('Character level must be at least 1')
    }

    // Validate ability scores
    const maxScore = this.getMaxAbilityScore()
    Object.keys(character.abilities).forEach((ability) => {
      const score = character.abilities[ability]
      if (score < 1 || score > maxScore) {
        errors.push(`${ability} score must be between 1 and ${maxScore}`)
      }
    })

    // Validate hit points
    if (character.hitPoints.current < 0) {
      errors.push('Current hit points cannot be negative')
    }

    if (character.hitPoints.maximum < 1) {
      errors.push('Maximum hit points must be at least 1')
    }

    return errors
  }

  /**
   * Validate and clean entity indexes against authoritative stores
   * Moves invalid spells to customSpells, removes invalid indexes from features/feats
   * @param {Object} characterData Character data to validate
   * @param {string} versionId Version ID for store paths (e.g., 'dnd_5e')
   * @returns {Promise<Object>} { cleanedData, report }
   */
  async validateAndCleanIndexes(characterData, versionId = 'dnd_5e') {
    try {
      // Targeted clone: only clone the nested paths we will mutate (spells, features, feats)
      const cleanedData = targetedCloneForMutation(characterData, {
        arrays: ['classes', 'features', 'feats', 'inventory', 'items'],
        objects: ['spells', 'customSpells', 'equipment', 'traits', 'abilities', 'skills']
      })
      const report = {
        invalidSpells: [],
        invalidFeatures: [],
        invalidFeats: [],
        invalidItems: [],
        movedToCustomSpells: [],
        removedIndexes: []
      }

      // Static imports for stores (Vite doesn't support dynamic template literal imports at runtime)
      // This service only handles dnd_5e, so we can safely use static imports
      const { useSpellStore } = await import('@/stores/versions/dnd_5e/spellStore')
      const { useFeatureStore } = await import('@/stores/versions/dnd_5e/featureStore')
      const { useFeatStore } = await import('@/stores/versions/dnd_5e/featStore')
      const { useClassStore } = await import('@/stores/versions/dnd_5e/classStore')

      const spellStore = useSpellStore()
      const featureStore = useFeatureStore()
      const featStore = useFeatStore()
      const classStore = useClassStore()

      // Ensure stores are loaded (pass versionId to fetch methods)
      if (!spellStore.spells || spellStore.spells.length === 0) {
        await spellStore.fetchSpells(versionId)
      }
      if (!featureStore.features || featureStore.features.length === 0) {
        await featureStore.fetchFeatures(versionId)
      }
      if (!featStore.list || featStore.list.length === 0) {
        await featStore.fetchFeats()
      }
      if (!classStore.classes || classStore.classes.length === 0) {
        await classStore.fetchClasses()
      }

      // Get valid indexes
      const validSpellIndexes = new Set(spellStore.spells.map((s) => s.index))
      const validFeatureIndexes = new Set(featureStore.features.map((f) => f.index))
      const validFeatIndexes = new Set(featStore.list.map((f) => f.index))
      const validClassNames = new Set(classStore.classes.map((c) => c.name.toLowerCase()))

      // Validate spells - move invalid to customSpells
      if (cleanedData.spells) {
        Object.keys(cleanedData.spells).forEach((level) => {
          if (Array.isArray(cleanedData.spells[level])) {
            const validSpells = []
            cleanedData.spells[level].forEach((spell) => {
              if (spell.index && !validSpellIndexes.has(spell.index)) {
                // Invalid spell - move to customSpells
                report.invalidSpells.push({ level, name: spell.name, index: spell.index })

                if (!cleanedData.customSpells) cleanedData.customSpells = {}
                if (!cleanedData.customSpells[level]) cleanedData.customSpells[level] = []

                const customSpell = { ...spell }
                delete customSpell.index // Remove invalid index
                cleanedData.customSpells[level].push(customSpell)
                report.movedToCustomSpells.push({ level, name: spell.name })
              } else {
                validSpells.push(spell)
              }
            })
            cleanedData.spells[level] = validSpells
          }
        })
      }

      // Validate features - remove invalid indexes
      if (Array.isArray(cleanedData.features)) {
        cleanedData.features = cleanedData.features.map((feature) => {
          if (feature.index && !validFeatureIndexes.has(feature.index)) {
            report.invalidFeatures.push({ name: feature.name, index: feature.index })
            report.removedIndexes.push({
              type: 'feature',
              name: feature.name,
              index: feature.index
            })
            const cleaned = { ...feature }
            delete cleaned.index
            return cleaned
          }
          return feature
        })
      }

      // Validate feats - remove invalid indexes
      if (Array.isArray(cleanedData.feats)) {
        cleanedData.feats = cleanedData.feats.map((feat) => {
          if (feat.index && !validFeatIndexes.has(feat.index)) {
            report.invalidFeats.push({ name: feat.name, index: feat.index })
            report.removedIndexes.push({ type: 'feat', name: feat.name, index: feat.index })
            const cleaned = { ...feat }
            delete cleaned.index
            return cleaned
          }
          return feat
        })
      }

      // Validate classes - flag invalid
      if (Array.isArray(cleanedData.classes)) {
        cleanedData.classes = cleanedData.classes.map((cls) => {
          if (cls.name && !validClassNames.has(cls.name.toLowerCase())) {
            return { ...cls, _invalidClass: true }
          }
          return cls
        })
      }

      console.log('Validation report:', report)
      return { cleanedData, report }
    } catch (error) {
      console.error('Error validating indexes:', error)
      // Return original data if validation fails
      return {
        cleanedData: characterData,
        report: {
          error: error.message,
          invalidSpells: [],
          invalidFeatures: [],
          invalidFeats: [],
          invalidItems: [],
          movedToCustomSpells: [],
          removedIndexes: []
        }
      }
    }
  }

  /**
   * Get character summary for D&D 5e
   * @param {Object} character Character object
   * @returns {Object} Character summary
   */
  getCharacterSummary(character) {
    const classString = character.classes
      ? character.classes.map((c) => `${c.name} ${c.level}`).join(', ')
      : 'No Class'

    return {
      name: character.name,
      race: character.race,
      class: classString,
      level: character.level,
      hitPoints: `${character.hitPoints.current}/${character.hitPoints.maximum}`,
      armorClass: character.armorClass,
      speed: character.speed,
      passivePerception: this.getPassivePerception(character),
      proficiencyBonus: this.calculateProficiencyBonus(character.level)
    }
  }

  /**
   * Calculate total character level (for multiclassing)
   * @param {Object} character Character object
   * @returns {number} Total character level
   */
  getTotalLevel(character) {
    if (!character.classes || character.classes.length === 0) return 1

    return character.classes.reduce((total, characterClass) => {
      return total + (characterClass.level || 1)
    }, 0)
  }

  /**
   * Get character's primary class
   * @param {Object} character Character object
   * @returns {Object} Primary class object
   */
  getPrimaryClass(character) {
    if (!character.classes || character.classes.length === 0) return null

    // Return the class with the highest level
    return character.classes.reduce((primary, current) => {
      return current.level > primary.level ? current : primary
    })
  }

  /**
   * Check if character is multiclassed
   * @param {Object} character Character object
   * @returns {boolean} True if character has multiple classes
   */
  isMulticlassed(character) {
    return character.classes && character.classes.length > 1
  }

  /**
   * Get a short rules snippet for a 5e condition
   * @param {string} name Condition name (case-insensitive)
   * @returns {string} short description
   */
  getConditionDescription(name) {
    if (!name) return ''
    const key = String(name).trim().toLowerCase()

    // Prefer authoritative descriptions from the conditions store (list or warmed details)
    try {
      const store = getLoadedStore('condition')
      // If condition store isn't synchronously available, attempt to read it asynchronously
      if (!store) return ''
      // Try memory detail first
      const detail = store?.conditionDetails?.[key]
      if (detail && Array.isArray(detail.desc) && detail.desc.length) return detail.desc.join('\n')

      // Try list entry by index
      const fromIdx =
        typeof store.getConditionByIndex === 'function' ? store.getConditionByIndex(key) : null
      if (fromIdx && Array.isArray(fromIdx.desc) && fromIdx.desc.length)
        return fromIdx.desc.join('\n')

      // Try list entry by name (case-insensitive)
      const fromName =
        typeof store.getConditionByName === 'function' ? store.getConditionByName(name) : null
      if (fromName && Array.isArray(fromName.desc) && fromName.desc.length)
        return fromName.desc.join('\n')
    } catch {
      /* no-op; fall through */
    }

    // No static fallback: return empty string when store has no data
    return ''
  }

  /**
   * Get the list of 5e exhaustion penalties for a given level (2014 rules, cumulative)
   * @param {number} level 0–6
   * @returns {string[]} array of penalty lines
   */
  getExhaustionPenalties(level) {
    const n = Math.max(0, Math.min(6, Number(level) || 0))
    const steps = [
      '1: Disadvantage on ability checks',
      '2: Speed halved',
      '3: Disadvantage on attack rolls and saving throws',
      '4: Hit point maximum halved',
      '5: Speed reduced to 0',
      '6: Death'
    ]
    return n === 0 ? ['No exhaustion'] : steps.slice(0, n)
  }

  /**
   * Build a minimal AI image context for artwork generation (do NOT include full character JSON)
   * Only includes selected descriptive fields and equipped items/armor, plus avatar URL.
   * @param {Object} character
   * @returns {Object}
   */
  buildAiImageContext(character) {
    const c = character || {}
    // Classes: keep only name and level
    const classes = Array.isArray(c.classes)
      ? c.classes
          .map((cl) => ({
            name: String(cl?.name || '').trim(),
            level: Number(cl?.level || 0) || undefined
          }))
          .filter((x) => x.name)
      : []

    // Equipped items/armor best-effort from inventory
    const inv = Array.isArray(c.inventory) ? c.inventory : []
    const equipped = inv.filter((it) => it && (it.equipped === true || it.isEquipped === true))
    const equippedItems = equipped
      .map((it) => String(it.name || it.item || '').trim())
      .filter(Boolean)
    const equippedArmor = equipped
      .filter((it) => /armor/i.test(String(it.type || it.category || it.subtype || it.name || '')))
      .map((it) => String(it.name || '').trim())
      .filter(Boolean)

    // Appearance details (best-effort)
    const eyes = String(c.eyes || '').trim() || undefined
    const hair = String(c.hair || '').trim() || undefined
    const skin = String(c.skin || '').trim() || undefined
    const distinguishing =
      String(c.distinguishingMarks || c.distinguishing || '').trim() || undefined
    const appearance = String(c.appearance || '').trim() || undefined

    // Dimensions/size
    const height = c.height ?? undefined
    const heightUnit = c.heightUnit ?? undefined
    const weight = c.weight ?? undefined
    const weightUnit = c.weightUnit ?? undefined
    const size = c.size ?? undefined

    return {
      name: c.name || undefined,
      race: c.race || undefined,
      subrace: c.subrace || undefined,
      background: c.background || undefined,
      alignment: c.alignment || undefined,
      age: c.age || undefined,
      classes,
      personalityTraits: c.personalityTraits || undefined,
      ideals: c.ideals || undefined,
      bonds: c.bonds || undefined,
      flaws: c.flaws || undefined,
      backstory: c.backstory || c.description || undefined,
      height,
      heightUnit,
      weight,
      weightUnit,
      size,
      eyes,
      hair,
      skin,
      distinguishingFeatures: distinguishing,
      appearance,
      equippedItems: equippedItems.length ? equippedItems : undefined,
      equippedArmor: equippedArmor.length ? equippedArmor : undefined,
      avatarUrl: c.avatar || undefined
    }
  }

  /**
   * Build a compact text context for AI text-generation tasks.
   * This intentionally omits large arrays and truncates long fields.
   */
  buildAiTextContext(character) {
    const c = character || {}
    const backstory = c.backstory || c.description || ''
    const appearance = c.appearance || ''
    const personality = c.personalityTraits || ''

    const classes = Array.isArray(c.classes)
      ? c.classes
          .map((cl) => ({
            name: String(cl?.name || '').trim(),
            level: Number(cl?.level || 0) || undefined
          }))
          .filter((x) => x.name)
      : []

    return {
      name: c.name || undefined,
      race: c.race || undefined,
      classes,
      appearance: typeof appearance === 'string' ? appearance.slice(0, 800) : undefined,
      backstory: typeof backstory === 'string' ? backstory.slice(0, 1200) : undefined,
      personalityTraits: typeof personality === 'string' ? personality.slice(0, 400) : undefined,
      avatarUrl: c.avatar || undefined
    }
  }
}

// Export singleton instance
export default new DnD5eCharacterService()
